/**
 * The company logo bands shown across the marketing surfaces.
 *
 * PROVENANCE RULE — read before adding anyone.
 * Every company here must be a real SpotMe host or renter, verifiable against
 * production: a host team with listings, or a renter with a checkout or a closed
 * deal. "In the pipeline", "we had a call", and "they're on the prospecting
 * list" are NOT qualifying. A logo band is an advertising claim, and an
 * unfounded one is publicidad engañosa under the LFPC — see root `CLAUDE.md` →
 * "Code ↔ Terms ↔ Marketing ↔ Help Center".
 *
 * The test is "is there a real business relationship", NOT "is inventory live
 * this minute". Listings move in and out of draft constantly — a host we work
 * with whose listings are paused is still a customer and stays in the band.
 * `yarn logo-band-roster`'s churn check flags zero-live hosts for REVIEW, and a
 * human decides; it is not an instruction to delete. (Marmar and Cuadro were
 * removed on that signal alone in the first pass and had to be put back — both
 * are hosts we actively work with.) What does NOT qualify is a company with no
 * relationship at all: the band this replaced carried six brands (Altio,
 * Lobatón, Numa, One Development, Orange, Plate) that had no team record on the
 * platform whatsoever.
 *
 * Roster verified against production 2026-08-02 (151 teams / 1,363 locations /
 * 1,153 checkouts / 11 closed deals) via `yarn logo-band-roster` in
 * `spotme-server`. Re-verify quarterly and after any supply push.
 *
 * Full procedure, the surface->band->caption table, and the judgement calls the
 * script cannot make: `docs/logo-bands.md`.
 *
 * Artwork: one monochrome alpha mask per company under
 * `public/images/media/logos/mask/`, generated by `yarn fetch-logos`. The band
 * paints them with `currentColor`, so a surface tints the whole strip by setting
 * a text colour — there is no per-colour copy of each file.
 *
 * The mask/size classes are written out as complete literals on purpose:
 * Tailwind only sees class names it can find as whole strings in source, so
 * building them by interpolation would silently produce unstyled logos.
 */

export type LogoBandId =
  | 'supply'
  | 'supplyStorage'
  | 'supplyParking'
  | 'demand'
  | 'demandFleet'

export type BandLogo = {
  /** Company name — becomes the accessible name of the mark. */
  readonly name: string
  /** Complete Tailwind classes: mask source + intrinsic box. */
  readonly className: string
}

/**
 * Hosts, mixed verticals — the biggest and most recognisable names we have.
 * For host surfaces that are NOT vertical-specific (`/anfitrion`, `/broker`,
 * `/negocio`), where the reader may own any kind of space.
 *
 * Only ever rendered on `/anfitrion/*`: hosts are anonymous to renters by
 * policy, so a host mark must never appear on a renter-facing surface.
 */
const SUPPLY: readonly BandLogo[] = [
  {
    name: 'Entra Estacionamientos',
    className:
      '[mask-image:url(/images/media/logos/mask/entra.png)] w-[67px] h-[32px]'
  },
  {
    name: 'INKO',
    className:
      '[mask-image:url(/images/media/logos/mask/inko.png)] w-[69px] h-[31px]'
  },
  {
    name: 'Imobilem',
    className:
      '[mask-image:url(/images/media/logos/mask/imobilem.png)] w-[104px] h-[20px]'
  },
  {
    name: 'Grupo Delta',
    className:
      '[mask-image:url(/images/media/logos/mask/grupo-delta.png)] w-[63px] h-[33px]'
  },
  {
    name: 'Sho Inmuebles',
    className:
      '[mask-image:url(/images/media/logos/mask/sho-inmuebles.png)] w-[47px] h-[45px]'
  },
  {
    name: 'AVC Industrial',
    className:
      '[mask-image:url(/images/media/logos/mask/avc-industrial.png)] w-[31px] h-[56px]'
  },
  {
    name: 'ELCABA Storage',
    className:
      '[mask-image:url(/images/media/logos/mask/elcaba.png)] w-[79px] h-[27px]'
  },
  {
    name: 'Marmar',
    className:
      '[mask-image:url(/images/media/logos/mask/marmur.png)] w-[76px] h-[28px]'
  },
  {
    name: 'Torobox',
    className:
      '[mask-image:url(/images/media/logos/mask/torobox.png)] w-[122px] h-[17px]'
  },
  {
    name: 'Grupo Inmobiliario Monterrey',
    className:
      '[mask-image:url(/images/media/logos/mask/gim.png)] w-[72px] h-[29px]'
  },
  {
    name: 'Kubox Mini Bodegas',
    className:
      '[mask-image:url(/images/media/logos/mask/kubox.png)] w-[50px] h-[43px]'
  },
  {
    name: 'Wor Capital',
    className:
      '[mask-image:url(/images/media/logos/mask/wor-capital.png)] w-[46px] h-[46px]'
  },
  {
    name: 'Elevo Grupo Inmobiliario',
    className:
      '[mask-image:url(/images/media/logos/mask/elevo.png)] w-[83px] h-[26px]'
  },
  {
    name: 'Gerencia RED',
    className:
      '[mask-image:url(/images/media/logos/mask/gerencia-red.png)] w-[72px] h-[29px]'
  },
  {
    name: 'Spakio',
    className:
      '[mask-image:url(/images/media/logos/mask/spakio.png)] w-[43px] h-[49px]'
  },
  {
    name: 'Alfrigo',
    className:
      '[mask-image:url(/images/media/logos/mask/alfrigo.png)] w-[55px] h-[39px]'
  }
]

/**
 * Self-storage / mini-bodega operators — the peer band for
 * `/anfitrion/tradicional`, whose copy addresses self-storage operators
 * specifically ("llena tus unidades de self-storage").
 *
 * Membership is the VERTICAL a host actually operates (`SELF_STORAGE_UNIT`
 * dominant in production), NOT the commercial model. Two consequences:
 *
 *  - Operators on any contract belong here. A mini-bodega owner recognises a
 *    peer; which annex we signed is invisible to them.
 *  - A Traditional-CONTRACT host in another vertical does NOT belong. Entra
 *    Estacionamientos is our biggest Traditional-model win, but it runs
 *    PARKING (937 of 937 units) and is no competitor to a mini-bodega brand —
 *    it leads SUPPLY_PARKING instead. Alfrigo is excluded for the same reason:
 *    Anexo C and a `SELF_STORAGE_UNIT` tag, but a cold-storage business.
 *    Trust the vertical the host operates, not the enum or the contract.
 */
const SUPPLY_STORAGE: readonly BandLogo[] = [
  {
    name: 'INKO',
    className:
      '[mask-image:url(/images/media/logos/mask/inko.png)] w-[69px] h-[31px]'
  },
  {
    name: 'Grupo Delta',
    className:
      '[mask-image:url(/images/media/logos/mask/grupo-delta.png)] w-[63px] h-[33px]'
  },
  {
    name: 'ELCABA Storage',
    className:
      '[mask-image:url(/images/media/logos/mask/elcaba.png)] w-[79px] h-[27px]'
  },
  {
    name: 'Torobox',
    className:
      '[mask-image:url(/images/media/logos/mask/torobox.png)] w-[122px] h-[17px]'
  },
  {
    name: 'Kubox Mini Bodegas',
    className:
      '[mask-image:url(/images/media/logos/mask/kubox.png)] w-[50px] h-[43px]'
  },
  {
    name: 'Wor Capital',
    className:
      '[mask-image:url(/images/media/logos/mask/wor-capital.png)] w-[46px] h-[46px]'
  },
  {
    name: 'Espacia',
    className:
      '[mask-image:url(/images/media/logos/mask/espacia.png)] w-[49px] h-[43px]'
  },
  {
    name: 'Kanuby Mini Bodegas',
    className:
      '[mask-image:url(/images/media/logos/mask/kanuby.png)] w-[100px] h-[21px]'
  },
  {
    name: 'Mex Storage',
    className:
      '[mask-image:url(/images/media/logos/mask/mex-storage.png)] w-[37px] h-[56px]'
  },
  {
    name: 'Eco Bodegas',
    className:
      '[mask-image:url(/images/media/logos/mask/eco-bodegas.png)] w-[110px] h-[19px]'
  },
  {
    name: 'Grupo DAGS',
    className:
      '[mask-image:url(/images/media/logos/mask/grupo-dags.png)] w-[44px] h-[48px]'
  },
  {
    name: 'Boxmi',
    className:
      '[mask-image:url(/images/media/logos/mask/boxmi.png)] w-[47px] h-[45px]'
  },
  {
    name: 'Spakio',
    className:
      '[mask-image:url(/images/media/logos/mask/spakio.png)] w-[43px] h-[49px]'
  }
]

/**
 * Parking operators — the peer band for `/anfitrion/estacionamiento`.
 *
 * Several of these read as generic real-estate names but are parking-dominant
 * in production, which is the only thing that decides membership: Imobilem is
 * 513 units of PARKING, Marmar 233, GIM 153, Gerencia RED and Cuadro likewise.
 * Do not reassign them by how the brand sounds.
 */
const SUPPLY_PARKING: readonly BandLogo[] = [
  {
    name: 'Entra Estacionamientos',
    className:
      '[mask-image:url(/images/media/logos/mask/entra.png)] w-[67px] h-[32px]'
  },
  {
    name: 'Imobilem',
    className:
      '[mask-image:url(/images/media/logos/mask/imobilem.png)] w-[104px] h-[20px]'
  },
  {
    name: 'Marmar',
    className:
      '[mask-image:url(/images/media/logos/mask/marmur.png)] w-[76px] h-[28px]'
  },
  {
    name: 'Grupo Inmobiliario Monterrey',
    className:
      '[mask-image:url(/images/media/logos/mask/gim.png)] w-[72px] h-[29px]'
  },
  {
    name: 'Gerencia RED',
    className:
      '[mask-image:url(/images/media/logos/mask/gerencia-red.png)] w-[72px] h-[29px]'
  },
  {
    name: 'Cuadro',
    className:
      '[mask-image:url(/images/media/logos/mask/cuadro.png)] w-[89px] h-[24px]'
  },
  {
    name: 'Desarrollos 10 Doce',
    className:
      '[mask-image:url(/images/media/logos/mask/desarrollos-10-doce.png)] w-[48px] h-[44px]'
  },
  {
    name: 'Punto Valle',
    className:
      '[mask-image:url(/images/media/logos/mask/punto-valle.png)] w-[46px] h-[46px]'
  },
  {
    name: 'Chroma San Pedro',
    className:
      '[mask-image:url(/images/media/logos/mask/chroma.png)] w-[46px] h-[46px]'
  }
]

/**
 * Renters, mixed — companies that rent space through SpotMe. For renter
 * surfaces where the audience isn't a single vertical (home, city/hood search,
 * a-domicilio, 3PL) and for host pages whose reader's question is "who will
 * rent my space?".
 *
 * Grupo Rivero is a real renter (closed deal, ~$76.5k/mo) and its franchised
 * dealership marques appear alongside it. Two rules keep that defensible:
 *
 *  1. Every marque is labelled "<brand> Rivero", never the bare brand — the
 *     renter is Rivero's dealership, not the OEM. Volvo Suecia Car follows the
 *     same rule; never shorten it to "Volvo".
 *  2. The order interleaves them with non-car renters ON PURPOSE, because this
 *     roster serves generic surfaces where five consecutive car marques would
 *     make SpotMe read as a car-parking company. Re-interleave when you add
 *     someone; don't append.
 *
 * Carz México and Volvo Suecia Car are INDEPENDENT of Rivero and sit outside
 * the Rivero run so they don't read as more Rivero marques.
 */
const DEMAND: readonly BandLogo[] = [
  {
    name: 'Rappi',
    className:
      '[mask-image:url(/images/media/logos/mask/rappi.png)] w-[71px] h-[30px]'
  },
  {
    name: 'Carz México',
    className:
      '[mask-image:url(/images/media/logos/mask/carz.png)] w-[93px] h-[23px]'
  },
  {
    name: 'Nissan Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-nissan.png)] w-[50px] h-[42px]'
  },
  {
    name: 'TDM Sostenible',
    className:
      '[mask-image:url(/images/media/logos/mask/tdm-sostenible.png)] w-[65px] h-[33px]'
  },
  {
    name: 'Ópticas Devlyn',
    className:
      '[mask-image:url(/images/media/logos/mask/devlyn.png)] w-[111px] h-[19px]'
  },
  {
    name: 'Chevrolet Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-chevrolet.png)] w-[64px] h-[33px]'
  },
  {
    name: 'Bosch',
    className:
      '[mask-image:url(/images/media/logos/mask/bosch.png)] w-[46px] h-[46px]'
  },
  {
    name: 'Volvo Suecia Car',
    className:
      '[mask-image:url(/images/media/logos/mask/volvo-suecia.png)] w-[46px] h-[46px]'
  },
  {
    name: 'GMC Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-gmc.png)] w-[99px] h-[21px]'
  },
  {
    name: 'Quimmco',
    className:
      '[mask-image:url(/images/media/logos/mask/quimmco.png)] w-[54px] h-[39px]'
  },
  {
    name: 'Grupo AGC',
    className:
      '[mask-image:url(/images/media/logos/mask/grupo-agc.png)] w-[63px] h-[34px]'
  },
  {
    name: 'Cadillac Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-cadillac.png)] w-[72px] h-[29px]'
  },
  {
    name: 'Grupo Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/grupo-rivero.png)] w-[128px] h-[17px]'
  },
  {
    name: 'Buick Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-buick.png)] w-[46px] h-[46px]'
  }
]

/**
 * Vehicle renters only — dealerships and fleet operators. For surfaces where
 * the reader is specifically about cars, and car-heavy is the POINT rather
 * than a distortion: `/pension-estacionamiento/flotillas`, the Parking pillar,
 * and `/anfitrion/estacionamiento` when framed as occupancy proof.
 */
const DEMAND_FLEET: readonly BandLogo[] = [
  {
    name: 'Carz México',
    className:
      '[mask-image:url(/images/media/logos/mask/carz.png)] w-[93px] h-[23px]'
  },
  {
    name: 'Nissan Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-nissan.png)] w-[50px] h-[42px]'
  },
  {
    name: 'Chevrolet Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-chevrolet.png)] w-[64px] h-[33px]'
  },
  {
    name: 'Volvo Suecia Car',
    className:
      '[mask-image:url(/images/media/logos/mask/volvo-suecia.png)] w-[46px] h-[46px]'
  },
  {
    name: 'GMC Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-gmc.png)] w-[99px] h-[21px]'
  },
  {
    name: 'Grupo Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/grupo-rivero.png)] w-[128px] h-[17px]'
  },
  {
    name: 'Cadillac Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-cadillac.png)] w-[72px] h-[29px]'
  },
  {
    name: 'Buick Rivero',
    className:
      '[mask-image:url(/images/media/logos/mask/rivero-buick.png)] w-[46px] h-[46px]'
  }
]

export const LOGO_BANDS: Record<LogoBandId, readonly BandLogo[]> = {
  supply: SUPPLY,
  supplyStorage: SUPPLY_STORAGE,
  supplyParking: SUPPLY_PARKING,
  demand: DEMAND,
  demandFleet: DEMAND_FLEET
}

/**
 * Which renter roster a PILLAR surface should show.
 *
 * The four pillars are four different audiences. Someone shopping pensión is
 * reassured by dealerships and fleets; someone shopping a mini-bodega or a nave
 * is not, and a strip of five car marques on a storage page actively
 * mis-positions SpotMe as a car-parking company. Parking therefore gets the
 * vehicle-only roster; everything else gets the mixed one.
 *
 * Takes the pillar as a plain string so the search pages (whose `pillar` comes
 * off the URL) can call it without importing the enum.
 */
export const bandForPillar = (pillar: string): LogoBandId =>
  pillar === 'pension-estacionamiento' ? 'demandFleet' : 'demand'

/** Caption to pair with `bandForPillar` — same reasoning, same split. */
export const captionForPillar = (
  pillar: string
): 'demandFleet' | 'demandStorage' | 'demand' => {
  if (pillar === 'pension-estacionamiento') return 'demandFleet'
  if (pillar === 'bodegas-en-renta' || pillar === 'naves-industriales') {
    return 'demandStorage'
  }
  return 'demand'
}
